'use client';

import {
  CartAndLineItemGroups,
  CommercetoolsOrder,
  LineItemGroup,
  SprdVoucherData,
} from '@/types/commercetools';
import { parseCompositionsJson, parseTrackingJson } from '@/utility/cartHelpers';
import { getGiftWrappingLineItem } from '@/utility/lineItems/lineItemHelpers';
import { CartProductData, OrderData } from '@/utility/tracking/types';

export const hashMessage = async (message: string) => {
  const encodedMessage = new TextEncoder().encode(message);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', encodedMessage);
  // convert buffer to byte array
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  // convert bytes to hex string
  return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const getSalutationId = (salutation?: string | null) => {
  switch (salutation) {
    case 'MALE':
      return 1;
    case 'FEMALE':
      return 2;
    case 'COMPANY':
      return 4;
    case 'OTHER':
      return 5;
    default:
      return 0;
  }
};

export async function transformToTrackingOrder(
  cartAndLineItemGroups: CartAndLineItemGroups,
  order: CommercetoolsOrder,
  paymentMethod: string,
  products: Product[]
): Promise<OrderData> {
  const { cart, lineItemGroups } = cartAndLineItemGroups;
  const hashedEmail = await hashMessage(cart.customerEmail);

  const { coupons, moneyTypeCoupons } = couponTrackingDataFromCart(cartAndLineItemGroups);

  const totalPrice = order.totalPrice.centAmount / 100;
  const totalPriceWithMoneyCoupons = getTotalPriceWithMoneyCoupon(moneyTypeCoupons, totalPrice);

  const totalPriceWithoutShippingExclVat =
    (order.taxedPrice.totalNet.centAmount - order.taxedShippingPrice.totalNet.centAmount) / 100;
  const totalPriceWithoutShippingExclVatWithMoneyCoupons = getTotalPriceWithMoneyCoupon(
    moneyTypeCoupons,
    totalPriceWithoutShippingExclVat
  );

  const giftWrappingItem = getGiftWrappingLineItem(cart.lineItems);

  return {
    id: order.id,
    hashedEmail,
    email: cart.customerEmail,
    orderItems: lineItemGroups.map((item) => transformLineItemGroup(item, products)),
    currency: cart.totalPrice.currencyCode,
    orderPrice: {
      totalPriceWithShippingInclVatExclMoneyTypeCoupons: totalPrice,
      totalPriceWithShippingInclVatInclMoneyTypeCoupons: totalPriceWithMoneyCoupons,
      totalPriceWithoutShippingExclVatExclMoneyTypeCoupons: totalPriceWithoutShippingExclVat,
      totalPriceWithoutShippingExclVatInclMoneyTypeCoupons:
        totalPriceWithoutShippingExclVatWithMoneyCoupons,
      totalTaxRates: cart.taxedPrice.taxPortions.map((portion) => ({
        name: portion.name,
        rate: portion.rate,
        amount: portion.amount.centAmount / 100,
      })),
      giftWrappingPrice: giftWrappingItem ? giftWrappingItem.totalPrice.centAmount / 100 : 0,
    },
    shipping: {
      salutation: cart.shippingAddress?.salutation ?? '',
      salutationId: getSalutationId(cart.shippingAddress?.salutation),
      countryCode: cart.shippingAddress?.country ?? '',
      // An order always has a shipping method; the fallbacks only satisfy the
      // optional `shippingInfo` that an in-progress cart can legitimately lack.
      shippingMethod: cart.shippingInfo?.shippingMethodName ?? '',
      priceItem: {
        vatIncluded: (cart.shippingInfo?.price.centAmount ?? 0) / 100,
        vatExcluded: (cart.shippingInfo?.taxedPrice.totalNet.centAmount ?? 0) / 100,
      },
    },
    paymentMethod,
    coupons,
    moneyTypeCoupons,
  };
}

export function couponTrackingDataFromCart(cartAndLineItemGroups: CartAndLineItemGroups) {
  const { cart, lineItemGroups } = cartAndLineItemGroups;
  const voucherJson = cart.custom.fields.sprdVoucherJson
    ? (JSON.parse(cart.custom.fields.sprdVoucherJson) as SprdVoucherData)
    : null;

  const coupons = cart.custom.fields.sprdVoucherList
    ? cart.custom.fields.sprdVoucherList.split(',')
    : [];
  const discountCoupons =
    coupons.length && voucherJson && voucherJson.redeemableMoneyTypeCoupons
      ? coupons.filter((c) => !voucherJson.redeemableMoneyTypeCoupons.some((m) => c === m.code))
      : coupons;

  return {
    coupons: discountCoupons.map((discountCoupon) => ({
      code: discountCoupon,
      amount:
        lineItemGroups.reduce(
          (result, group) => result + group.priceBreakdown.totalDirectDiscount,
          0
        ) / 100,
    })),
    moneyTypeCoupons:
      voucherJson && voucherJson.redeemableMoneyTypeCoupons
        ? voucherJson.redeemableMoneyTypeCoupons.map((c) => ({
            code: c.code,
            amount: c.appliedAmount.centAmount / 100,
          }))
        : undefined,
  };
}

type Coupon = { code: string; amount: number };

export const getTotalPriceWithMoneyCoupon = (
  moneyTypeCoupons: Coupon[] | undefined,
  totalPrice: number
) => {
  if (!moneyTypeCoupons) return totalPrice;

  return moneyTypeCoupons.reduce((a, b) => {
    return a - b.amount;
  }, totalPrice);
};

export function transformLineItemGroup(
  lineItemGroup: LineItemGroup,
  products: Product[]
): CartProductData {
  const product = products.find((p) => p.id === lineItemGroup.productId.toString());

  return {
    designs: parseCompositionsJson(lineItemGroup.lineItems[0]!),
    ...parseTrackingJson(lineItemGroup.lineItems[0]!),
    productLookupId: lineItemGroup.productLookupId,
    productId: lineItemGroup.productId,
    productTypeId: lineItemGroup.productTypeId,
    articleId:
      lineItemGroup.lineItems[0]!.custom.fields.sprdCompositionId ||
      lineItemGroup.lineItems[0]!.custom.fields.sprdProductLookupId,
    sellableId: lineItemGroup.sellableId,
    shopId: lineItemGroup.shopId.toString(),
    appearanceId: lineItemGroup.appearanceId,
    itemQuantity: lineItemGroup.groupQuantity,
    productDetails: {
      sizes: lineItemGroup.lineItems.map((item) => ({
        sizeId: item.variant.attributes.find((a) => a.name === 'sprdSizeId')?.value || 0,
        productCount: item.quantity,
      })),
      configurations: product?.configurations.map((c) => ({
        configurationType: c.type,
        printTypeId: c.printType.id,
      })),
    },
    itemPricing: {
      lineItemGroupInclVat: lineItemGroup.priceBreakdown.lineItemGroupInclVat / 100,
      lineItemGroupExclVat: lineItemGroup.priceBreakdown.lineItemGroupExclVat / 100,
      lineItemGroupTaxRates: lineItemGroup.priceBreakdown.lineItemGroupTaxRates,
      commissionInclVat: lineItemGroup.priceBreakdown.commissionInclVat / 100,
      commissionExclVat: lineItemGroup.priceBreakdown.commissionExclVat / 100,
      commissionTaxRates: lineItemGroup.priceBreakdown.commissionTaxRates,
      decorationInclVat: lineItemGroup.priceBreakdown.decorationInclVat / 100,
      decorationExclVat: lineItemGroup.priceBreakdown.decorationExclVat / 100,
      decorationTaxRates: lineItemGroup.priceBreakdown.decorationTaxRates,
      // totalDiscountAmount includes volume and direct discounts, but not money type coupons
      totalDiscountAmount: lineItemGroup.priceBreakdown.totalDiscount / 100,
      totalPriceIncludingVatExclDiscounts: lineItemGroup.groupTotalCentAmount / 100,
      totalVolumeDiscountAmount: lineItemGroup.priceBreakdown.totalVolumeDiscount / 100,
      // directDiscount includes discount vouchers
      totalDirectDiscountAmount: lineItemGroup.priceBreakdown.totalDirectDiscount / 100,
    },
  };
}
